import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "framer-motion";
import {
  CalendarDays,
  Clock,
  MapPin,
  MapPinned,
  User,
  Phone,
  FileText,
  HeartHandshake,
  Briefcase,
  Car,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  {
    value: "medical",
    label: "Transport Médical (VSL)",
    icon: HeartHandshake,
    description: "Conventionné CPAM",
    color: "text-emerald-400",
    borderColor: "border-emerald-500/50",
    bgColor: "bg-emerald-500/10",
  },
  {
    value: "professional",
    label: "Course Professionnelle",
    icon: Briefcase,
    description: "Gares, Aéroports, RDV",
    color: "text-blue-400",
    borderColor: "border-blue-500/50",
    bgColor: "bg-blue-500/10",
  },
  {
    value: "private",
    label: "Course Privée",
    icon: Car,
    description: "Déplacements personnels",
    color: "text-amber-400",
    borderColor: "border-amber-500/50",
    bgColor: "bg-amber-500/10",
  },
];

const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    TIME_SLOTS.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

export default function BookingForm() {
  const [form, setForm] = useState({
    passenger_name: "",
    passenger_phone: "",
    pickup_address: "",
    dropoff_address: "",
    date: null,
    time: "",
    service_type: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.passenger_name || !form.passenger_phone || !form.pickup_address || !form.dropoff_address || !form.date || !form.time || !form.service_type) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        date: format(form.date, "dd/MM/yyyy"),
      });
      setSuccess(true);
      toast.success("Réservation confirmée ! Nous vous contacterons rapidement.");
    } catch {
      toast.error("Erreur lors de la réservation. Réessayez ou appelez-nous.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section id="booking" className="py-16 md:py-24" data-testid="booking-success">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-card border border-emerald-500/30 rounded-2xl p-10 shadow-2xl">
            <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-3">
              Réservation enregistrée !
            </h2>
            <p className="text-zinc-400 text-lg mb-8">
              Votre demande a bien été prise en compte. Nous vous contacterons très rapidement pour confirmer votre course.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg px-6"
                onClick={() => {
                  setSuccess(false);
                  setForm({
                    passenger_name: "",
                    passenger_phone: "",
                    pickup_address: "",
                    dropoff_address: "",
                    date: null,
                    time: "",
                    service_type: "",
                    notes: "",
                  });
                }}
                data-testid="new-booking-btn"
              >
                Nouvelle réservation
              </Button>
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 rounded-lg px-6"
                asChild
              >
                <a href="tel:0652620747" data-testid="success-call-btn">
                  <Phone className="h-4 w-4 mr-2" />
                  Appeler
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="py-20 md:py-28" data-testid="booking-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-amber-400 font-tagline text-sm tracking-[0.3em] uppercase font-semibold">
            Réservation en ligne
          </span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">
            Réservez votre course
          </h2>
          <p className="text-zinc-400 mt-3 text-base md:text-lg">
            Remplissez le formulaire ci-dessous et nous vous recontactons rapidement.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="bg-card/80 backdrop-blur-md border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl space-y-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          data-testid="booking-form"
        >
          {/* Service Type Selection */}
          <div>
            <Label className="text-sm font-semibold text-zinc-300 mb-3 block">
              Type de course *
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {SERVICE_TYPES.map((st) => {
                const selected = form.service_type === st.value;
                return (
                  <button
                    type="button"
                    key={st.value}
                    onClick={() => update("service_type", st.value)}
                    className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      selected
                        ? `${st.borderColor} ${st.bgColor}`
                        : "border-white/10 hover:border-white/25 bg-transparent"
                    }`}
                    data-testid={`service-type-${st.value}`}
                  >
                    <st.icon className={`h-7 w-7 ${selected ? st.color : "text-zinc-500"}`} />
                    <span className={`text-sm font-semibold ${selected ? "text-white" : "text-zinc-400"}`}>
                      {st.label}
                    </span>
                    <span className={`text-xs ${selected ? "text-zinc-300" : "text-zinc-600"}`}>
                      {st.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <Separator className="bg-white/10" />

          {/* Personal Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="text-sm text-zinc-300 mb-2 block">
                <User className="inline h-4 w-4 mr-1 text-amber-400" />
                Nom et Prénom *
              </Label>
              <Input
                id="name"
                placeholder="Votre nom complet"
                value={form.passenger_name}
                onChange={(e) => update("passenger_name", e.target.value)}
                className="bg-secondary/50 border-white/10 focus:border-amber-500 h-12 rounded-lg placeholder:text-zinc-600"
                data-testid="input-name"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-sm text-zinc-300 mb-2 block">
                <Phone className="inline h-4 w-4 mr-1 text-amber-400" />
                Téléphone *
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="06 XX XX XX XX"
                value={form.passenger_phone}
                onChange={(e) => update("passenger_phone", e.target.value)}
                className="bg-secondary/50 border-white/10 focus:border-amber-500 h-12 rounded-lg placeholder:text-zinc-600"
                data-testid="input-phone"
              />
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="pickup" className="text-sm text-zinc-300 mb-2 block">
                <MapPin className="inline h-4 w-4 mr-1 text-emerald-400" />
                Adresse de départ *
              </Label>
              <AddressAutocomplete
                id="pickup"
                value={form.pickup_address}
                onChange={(v) => update("pickup_address", v)}
                placeholder="Tapez votre adresse de départ..."
                icon={MapPin}
                iconColor="text-emerald-400"
                testId="input-pickup"
              />
            </div>
            <div>
              <Label htmlFor="dropoff" className="text-sm text-zinc-300 mb-2 block">
                <MapPinned className="inline h-4 w-4 mr-1 text-red-400" />
                Adresse d'arrivée *
              </Label>
              <AddressAutocomplete
                id="dropoff"
                value={form.dropoff_address}
                onChange={(v) => update("dropoff_address", v)}
                placeholder="Tapez votre adresse d'arrivée..."
                icon={MapPinned}
                iconColor="text-red-400"
                testId="input-dropoff"
              />
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-sm text-zinc-300 mb-2 block">
                <CalendarDays className="inline h-4 w-4 mr-1 text-amber-400" />
                Date de prise en charge *
              </Label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-12 bg-secondary/50 border-white/10 hover:bg-secondary/70 rounded-lg font-normal"
                    data-testid="date-picker-btn"
                  >
                    <CalendarDays className="mr-2 h-4 w-4 text-zinc-500" />
                    {form.date ? (
                      <span className="text-white">{format(form.date, "EEEE d MMMM yyyy", { locale: fr })}</span>
                    ) : (
                      <span className="text-zinc-500">Sélectionner une date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-card border-white/10" align="start">
                  <Calendar
                    mode="single"
                    selected={form.date}
                    onSelect={(d) => {
                      update("date", d);
                      setDateOpen(false);
                    }}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    locale={fr}
                    data-testid="calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-sm text-zinc-300 mb-2 block">
                <Clock className="inline h-4 w-4 mr-1 text-amber-400" />
                Heure de prise en charge *
              </Label>
              <Popover open={timeOpen} onOpenChange={setTimeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-12 bg-secondary/50 border-white/10 hover:bg-secondary/70 rounded-lg font-normal"
                    data-testid="time-picker-btn"
                  >
                    <Clock className="mr-2 h-4 w-4 text-zinc-500" />
                    {form.time ? (
                      <span className="text-white">{form.time}</span>
                    ) : (
                      <span className="text-zinc-500">Sélectionner une heure</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0 bg-card border-white/10 max-h-64 overflow-y-auto" align="start">
                  <div className="p-1">
                    {TIME_SLOTS.map((t) => (
                      <button
                        type="button"
                        key={t}
                        onClick={() => {
                          update("time", t);
                          setTimeOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                          form.time === t
                            ? "bg-amber-500/20 text-amber-400"
                            : "text-zinc-300 hover:bg-white/5"
                        }`}
                        data-testid={`time-slot-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-sm text-zinc-300 mb-2 block">
              <FileText className="inline h-4 w-4 mr-1 text-zinc-500" />
              Informations complémentaires
            </Label>
            <textarea
              id="notes"
              rows={3}
              placeholder="Précisions sur la course, numéro de vol, étage, mobilité réduite..."
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="flex w-full rounded-lg border bg-secondary/50 border-white/10 px-3 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50 resize-none"
              data-testid="input-notes"
            />
          </div>

          {/* Submit */}
          <Button
            type="submit"
            size="lg"
            disabled={loading}
            className="relative w-full bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase tracking-wider h-14 rounded-xl shadow-[0_0_30px_rgba(245,158,11,0.3)] hover:shadow-[0_0_50px_rgba(245,158,11,0.6)] transition-all duration-300 text-base overflow-hidden group"
            data-testid="submit-booking-btn"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-amber-400/0 via-white/30 to-amber-400/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
            {loading ? (
              <span className="relative flex items-center justify-center">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Envoi en cours...
              </span>
            ) : (
              <span className="relative">Confirmer la réservation</span>
            )}
          </Button>
        </motion.form>
      </div>
    </section>
  );
}
